import React, { useMemo, useState } from "react";
import { useBLE } from "../../../hooks/useBle"; // Adjust the import path as needed
import {
  getTherapiesByBatch,
  TherapyEntry,
} from "../../../services/storageService";

// Types for the component props
interface TherapyDataCardProps {
  onExpand: () => void;
}

export const TherapyDataCard: React.FC<TherapyDataCardProps> = ({
  onExpand,
}) => {
  const {
    BLELogCount,
    serverSuccessLogCount,
    serverFailLogCount,
    batchCount,
    recentLog,
    batchesMetaData,
    storage,
  } = useBLE();
  const [expandedBatches, setExpandedBatches] = useState<
    Record<string, boolean>
  >({});
  const [loadingBatches, setLoadingBatches] = useState<Record<string, boolean>>(
    {}
  );

  const parseTherapyString = (therapyString: any) => {
    // Return empty array if therapyString is falsy (null, undefined, empty string)
    if (!therapyString) return [];

    // Split the therapy string by pipe character to get individual parameter entries
    const pairs = therapyString
      .split("|")
      .filter((item: any) => item.trim().length > 0);

    // Process each parameter entry
    return pairs.map((pair: any) => {
      // Find the index of the first colon in the pair
      const colonIndex = pair.indexOf(":");

      // If no colon is found, return the whole string as key with empty value
      if (colonIndex === -1) {
        return { key: pair, value: "" };
      }

      // Split the pair at only the first colon
      const key = pair.substring(0, colonIndex).trim();
      const value = pair.substring(colonIndex + 1).trim();

      return { key, value };
    });
  };

  const toggleBatchExpand = async (batchId: string) => {
    // Toggle the expanded state
    setExpandedBatches((prev) => ({
      ...prev,
      [batchId]: !prev[batchId],
    }));

    // If expanding and we don't have data yet, fetch it
    if (!expandedBatches[batchId] && !batchTherapies[batchId]) {
      setLoadingBatches((prev) => ({ ...prev, [batchId]: true }));

      if (storage) {
        const therapies = await getTherapiesByBatch(storage, batchId);
        setBatchTherapies((prev) => ({
          ...prev,
          [batchId]: therapies,
        }));
      }

      setLoadingBatches((prev) => ({ ...prev, [batchId]: false }));
    }
  };
  const [batchTherapies, setBatchTherapies] = useState<
    Record<string, TherapyEntry[]>
  >({});
  // Sort batches metadata by batch_id in descending order
  const sortedBatchesMetaData = useMemo(() => {
    if (!batchesMetaData || batchesMetaData.length === 0) return [];

    return [...batchesMetaData].sort((a, b) => {
      const numA = parseInt(a.batch_id);
      const numB = parseInt(b.batch_id);

      // If both are valid numbers, compare numerically
      if (!isNaN(numA) && !isNaN(numB)) {
        return numB - numA;
      }

      // If only one is a valid number, place numbers before non-numbers
      if (!isNaN(numA)) return -1;
      if (!isNaN(numB)) return 1;

      // If neither are valid numbers, sort alphabetically
      return b.batch_id.localeCompare(a.batch_id);
    });
  }, [batchesMetaData]);

  const ExpandIcon = () => (
    <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
      <rect width="30" height="30" rx="15" fill="#262626" />
      <path
        d="M20.8346 9.1665L15.8346 9.1665L15.8346 10.8332L19.168 10.8332L19.168 14.1665L20.8346 14.1665L20.8346 9.1665ZM14.168 19.1665L10.8346 19.1665L10.8346 15.8332L9.16797 15.8332L9.16797 20.8332L14.168 20.8332L14.168 19.1665Z"
        fill="#B0B4B7"
      />
    </svg>
  );

  const ChevronForward = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path
        d="M7.5 15L12.5 10L7.5 5"
        stroke="#01B7C4"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const ChevronDown = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path
        d="M5 7.5L10 12.5L15 7.5"
        stroke="#01B7C4"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const renderLogsSummary = () => (
    <div className="mb-6">
      <h4 className="text-white font-medium mb-4">Logs Summary</h4>
      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col items-center p-3 bg-gray-800 rounded-lg">
          <span className="text-gray-400 text-sm mb-1">BLE Logs</span>
          <span
            className="text-white font-bold text-lg px-3 py-1 rounded-full text-center"
            style={{ backgroundColor: "#01B7C4" }}
          >
            {BLELogCount}
          </span>
        </div>
        <div className="flex flex-col items-center p-3 bg-gray-800 rounded-lg">
          <span className="text-gray-400 text-sm mb-1">Success Logs</span>
          <span
            className="text-white font-bold text-lg px-3 py-1 rounded-full text-center"
            style={{ backgroundColor: "#10B981" }}
          >
            {serverSuccessLogCount}
          </span>
        </div>
        <div className="flex flex-col items-center p-3 bg-gray-800 rounded-lg">
          <span className="text-gray-400 text-sm mb-1">Failed Logs</span>
          <span
            className="text-white font-bold text-lg px-3 py-1 rounded-full text-center"
            style={{ backgroundColor: "#EF4444" }}
          >
            {serverFailLogCount}
          </span>
        </div>
        <div className="flex flex-col items-center p-3 bg-gray-800 rounded-lg">
          <span className="text-gray-400 text-sm mb-1">Batches</span>
          <span
            className="text-white font-bold text-lg px-3 py-1 rounded-full text-center"
            style={{ backgroundColor: "#8B5CF6" }}
          >
            {batchCount}
          </span>
        </div>
      </div>
    </div>
  );

  const renderRecentLog = () => {
    if (!recentLog) return null;

    return (
      <div className="mb-6">
        <div className="w-full h-px bg-gray-600 mb-4"></div>
        <h4 className="text-white font-medium mb-4">
          Log Event Id: {recentLog.batch_id || "N/A"}
        </h4>

        {recentLog.therapy && (
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="mb-4">
              <span className="text-gray-400 text-sm mb-2 block">
                Log String:
              </span>
              <div
                className="bg-gray-900 rounded p-3 max-h-24 overflow-y-auto text-white text-xs font-mono"
                style={{
                  scrollbarWidth: "thin",
                  scrollbarColor: "#4B5563 #1F2937",
                }}
              >
                <pre className="whitespace-pre-wrap">{recentLog.therapy}</pre>
              </div>
            </div>

            <div>
              <span className="text-gray-400 text-sm mb-2 block">
                Parameters:
              </span>
              <div className="flex flex-wrap gap-2">
                {parseTherapyString(recentLog.therapy).map(
                  (param: { key: string; value: string }, idx: number) => (
                    <div
                      key={idx}
                      className="bg-cyan-500 bg-opacity-20 border border-cyan-500 rounded-full px-3 py-1 flex items-center gap-2"
                    >
                      <span className="text-cyan-400 text-xs font-medium">
                        {param.key}:
                      </span>
                      <span className="text-white text-xs">{param.value}</span>
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderBatchHistory = () => (
    <div>
      <div className="w-full h-px bg-gray-600 mb-4"></div>
      <h4 className="text-white font-medium mb-4">Batch History</h4>

      {sortedBatchesMetaData.length > 0 ? (
        <div className="space-y-2">
          {sortedBatchesMetaData.map(
            (batch: { batch_id: string; entryCount: number }) => (
              <div
                key={batch.batch_id}
                className="bg-gray-800 rounded-lg overflow-hidden"
              >
                <button
                  className="w-full flex items-center justify-between p-4 hover:bg-gray-700 transition-colors"
                  onClick={() => toggleBatchExpand(batch.batch_id)}
                >
                  <div className="text-left">
                    <h5 className="text-white font-medium">
                      Batch: {batch.batch_id}
                    </h5>
                    <p className="text-gray-400 text-sm">
                      Entries: {batch.entryCount}
                    </p>
                  </div>
                  {expandedBatches[batch.batch_id] ? (
                    <ChevronDown />
                  ) : (
                    <ChevronForward />
                  )}
                </button>

                {expandedBatches[batch.batch_id] && (
                  <div className="border-t border-gray-700 p-4">
                    {batchTherapies[batch.batch_id]?.length > 0 ? (
                      <div
                        className="space-y-3 max-h-64 overflow-y-auto"
                        style={{
                          scrollbarWidth: "thin",
                          scrollbarColor: "#4B5563 #1F2937",
                        }}
                      >
                        {batchTherapies[batch.batch_id].map((therapy: any) => (
                          <div
                            key={therapy.event_id}
                            className="bg-gray-900 rounded-lg p-3"
                          >
                            <div className="text-cyan-400 text-sm font-medium mb-2">
                              Event ID: {therapy.event_id}
                            </div>

                            <div className="mb-3">
                              <span className="text-gray-400 text-xs mb-1 block">
                                Therapy String:
                              </span>
                              <div className="bg-black rounded p-2 max-h-20 overflow-y-auto">
                                <pre className="text-white text-xs font-mono whitespace-pre-wrap">
                                  {therapy.therapy}
                                </pre>
                              </div>
                            </div>

                            {therapy.therapy && (
                              <div>
                                <span className="text-gray-400 text-xs mb-1 block">
                                  Parameters:
                                </span>
                                <div className="flex flex-wrap gap-1">
                                  {parseTherapyString(therapy.therapy).map(
                                    (param: any, idx: number) => (
                                      <div
                                        key={idx}
                                        className="bg-cyan-500 bg-opacity-20 border border-cyan-500 rounded-full px-2 py-0.5 flex items-center gap-1"
                                      >
                                        <span className="text-cyan-400 text-xs">
                                          {param.key}:
                                        </span>
                                        <span className="text-white text-xs">
                                          {param.value}
                                        </span>
                                      </div>
                                    )
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <span className="text-gray-400 text-sm">
                          No therapy data available for this batch
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          )}
        </div>
      ) : (
        <div className="text-center py-8">
          <span className="text-gray-400">No batch data available</span>
        </div>
      )}
    </div>
  );

  const hasData =
    BLELogCount > 0 ||
    serverSuccessLogCount > 0 ||
    serverFailLogCount > 0 ||
    batchCount > 0;
  const showLogs = false; // Temporarily disable logs display
  if (!showLogs) {
    return <></>;
  }
  return (
    <div
      className="rounded-xl mb-4 overflow-hidden mx-auto"
      style={{ backgroundColor: "#1C1C1E", width: "100%" }}
    >
      <div
        className="flex justify-between items-center p-4 border-b"
        style={{ borderColor: "rgba(255, 255, 255, 0.1)" }}
      >
        <h3 className="text-white text-lg font-medium">
          DEVICE LOGS{" "}
          <span className="text-white/50 font-normal">(Coming Soon...)</span>
        </h3>
        {/* <div className="flex items-center gap-3">
    <button
      onClick={onExpand}
      className="hover:opacity-80 transition-opacity"
    >
      <ExpandIcon />
    </button>
  </div> */}
      </div>

      <div className="p-4">
        {hasData ? (
          <div>
            {/* {renderLogsSummary()} */}
            {renderRecentLog()}
            {/* {renderBatchHistory()} */}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16">
            <span className="text-gray-400">No therapy data available</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default TherapyDataCard;
